import { useCallback } from 'react';
import { graphql } from 'react-relay';

import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';

import type { useReadAndWriteContentQueryDataCacheReadContentFragment$data } from './__generated__/useReadAndWriteContentQueryDataCacheReadContentFragment.graphql.ts';
import type { useReadAndWriteContentQueryDataCacheWriteContentFragment$data } from './__generated__/useReadAndWriteContentQueryDataCacheWriteContentFragment.graphql.ts';

export const useReadAndWriteContentQueryDataCache = ({ query, contentId, spaceKey }) => {
	const readQueryInterop =
		useReadFragmentInterop<useReadAndWriteContentQueryDataCacheReadContentFragment$data>({
			fragmentSpec: graphql`
				fragment useReadAndWriteContentQueryDataCacheReadContentFragment on Content {
					body {
						dynamic {
							representation
							value
							webresource {
								superbatch {
									tags {
										js
										css
									}
								}
								tags {
									js
									css
								}
							}
						}
					}
				}
			`,
		});
	const writeFragmentInterop = useWriteFragmentInterop<
		Omit<useReadAndWriteContentQueryDataCacheWriteContentFragment$data, ' $fragmentType'>
	>({
		fragmentSpec: graphql`
			fragment useReadAndWriteContentQueryDataCacheWriteContentFragment on Content {
				body {
					dynamic {
						representation
						value
						webresource {
							superbatch {
								tags {
									js
									css
								}
							}
							tags {
								js
								css
							}
						}
					}
				}
			}
		`,
	});

	const readAndWriteQueryCache = useCallback(
		(newAdfDoc) => {
			const contentQueryResultCache = readQueryInterop({
				id: `Content:${contentId}`,
			});

			if (contentQueryResultCache?.body?.dynamic) {
				const newContentData: useReadAndWriteContentQueryDataCacheReadContentFragment$data = {
					...contentQueryResultCache,
					body: {
						dynamic: {
							representation: contentQueryResultCache.body.dynamic.representation,
							value: JSON.stringify(newAdfDoc),
							webresource: contentQueryResultCache.body.dynamic.webresource,
						},
					},
				};
				writeFragmentInterop({
					fragmentId: `Content:${contentId}`,
					data: newContentData,
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[query, contentId, spaceKey, readQueryInterop, writeFragmentInterop],
	);

	return readAndWriteQueryCache;
};
